import { TransitionService } from "@uirouter/angular";
import { Injectable, OnDestroy } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { CurrentUserService, IOnboardingMetadata, IOnboardingMetadataEntry } from "@gtmhub/users";
import { fromTransitionHook } from "@webapp/core/routing/rxjs";
import { SettingsVisibilityService } from "@webapp/navigation/services/settings/settings-visibility.service";
import {
  closeJourneyState,
  completeJourneyState,
  completeTransitionForState,
  createInitialOnboardingState,
  disabledJourneyState,
  getJourneyNextStep,
  getJourneyPreviousStep,
  pendingStartJourneyState,
} from "@webapp/shared/components/onboarding/onboarding-state.util";
import { IOnboardingJourneyState, KPIS_USER_SETTINGS_KEY } from "@webapp/shared/components/onboarding/onboarding.models";
import dayjs from "@webapp/shared/libs/dayjs";
import { KPIS_DETAIL_TOTAL_STEPS, KPIS_PROJECTIONS_ONBOARDING_JOURNEY_MAX_VIEWS } from "./kpis-onboarding.models";

@UntilDestroy()
@Injectable()
export class KpisOnboardingService implements OnDestroy {
  private onboardingMetadata: IOnboardingMetadataEntry;
  private markedForCompletion = false;
  private totalSteps = KPIS_DETAIL_TOTAL_STEPS;
  private onboardingState$: BehaviorSubject<IOnboardingJourneyState> = new BehaviorSubject<IOnboardingJourneyState>(createInitialOnboardingState(this.totalSteps));
  private skipSteps: number[] = [];
  private destroy$ = new Subject<void>();

  constructor(private currentUserService: CurrentUserService, private settingsVisibilityService: SettingsVisibilityService, transitionService: TransitionService) {
    // setting default value of the onboarding metadata for the specific journey
    const onboardingMetaData = {
      [KPIS_USER_SETTINGS_KEY]: { timesShown: 0, isCompleted: false, lastWatchedDate: null },
      ...this.currentUserService.getUserSetting<IOnboardingMetadata>("onboardingMetadata"),
    };

    this.onboardingMetadata = onboardingMetaData[KPIS_USER_SETTINGS_KEY];
    this.settingsVisibilityService.showSettings$.subscribe((showSettings) => {
      if (showSettings && this.shouldSeeJourneyAccordingToMetadata()) {
        this.updateState(pendingStartJourneyState(this.totalSteps, this.skipSteps));
      } else {
        this.updateState(disabledJourneyState(this.totalSteps));
      }
    });

    fromTransitionHook(transitionService, "onSuccess", {})
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        if (this.onboardingState$.value.stage === "PENDING_START") return;

        if (this.onboardingState$.value.stage === "IN_TRANSITION") {
          this.completeTransition();
        } else {
          this.close();
        }
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public getState$(): Observable<IOnboardingJourneyState> {
    return this.onboardingState$.asObservable();
  }

  public next(options: { inTransition: boolean; stepsDelta?: number }): void {
    this.updateState(getJourneyNextStep(this.onboardingState$.value, options));
  }

  public back(options: { inTransition: boolean; stepsDelta?: number }): void {
    this.updateState(getJourneyPreviousStep(this.onboardingState$.value, options));
  }

  public completeTransition(): void {
    this.updateState(completeTransitionForState(this.onboardingState$.value));
  }

  public close(): void {
    this.updateState(closeJourneyState(this.onboardingState$.value));
  }

  public complete(): void {
    this.markedForCompletion = true;
    this.onboardingMetadata.isCompleted = true;
    this.syncOnboardingMetadata();
    this.close();
  }

  public markAsShown(): void {
    this.onboardingMetadata.timesShown++;
    this.onboardingMetadata.lastWatchedDate = dayjs().utc().toISOString();
    this.onboardingMetadata.isCompleted = this.onboardingMetadata.timesShown >= KPIS_PROJECTIONS_ONBOARDING_JOURNEY_MAX_VIEWS;
    this.syncOnboardingMetadata();
  }

  private syncOnboardingMetadata(): void {
    this.currentUserService.setUserSetting({
      onboardingMetadata: { ...this.currentUserService.getUserSetting<IOnboardingMetadata>("onboardingMetadata"), [KPIS_USER_SETTINGS_KEY]: this.onboardingMetadata },
    });
  }

  private shouldSeeJourneyAccordingToMetadata(): boolean {
    const isCompleted = this.onboardingMetadata.isCompleted;
    const exceededMaxViews = this.onboardingMetadata.timesShown >= KPIS_PROJECTIONS_ONBOARDING_JOURNEY_MAX_VIEWS;
    const lastWatchedWithin24hours = dayjs().diff(this.onboardingMetadata.lastWatchedDate, "hours") <= 24;

    if (isCompleted || exceededMaxViews || lastWatchedWithin24hours) {
      return false;
    }

    return true;
  }

  private updateState(state: IOnboardingJourneyState): void {
    if (this.markedForCompletion) {
      this.onboardingState$.next(completeJourneyState(state));
      this.markedForCompletion = false;
    } else {
      this.onboardingState$.next(state);
    }
  }
}
